import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Accessibility, ChevronDown, ChevronRight, DoorOpen, Grab, LayoutGrid, Phone, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/siteConfig";
import { useQuote } from "./QuoteModal";
import { useContent } from "@/content/ContentContext";

const SERVICE_LABELS = ["Grab Rails", "Hand Rails", "Rubber Ramps"];
const SERVICE_LINKS = NAV_LINKS.filter((link) => SERVICE_LABELS.includes(link.label));
const TOP_LINKS = NAV_LINKS.filter((link) => !SERVICE_LABELS.includes(link.label));
const SERVICE_ICONS = { "Grab Rails": Grab, "Hand Rails": Accessibility, "Rubber Ramps": DoorOpen };
const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote } = useQuote();
  const { get } = useContent();
  const location = useLocation();
  const servicesRef = useRef(null);
  const phoneHref = get("site_phone_href", SITE.phoneHref);
  const phoneDisplay = get("site_phone_display", SITE.phoneDisplay);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!servicesOpen) return;
    const onPointerDown = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) setServicesOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setServicesOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [servicesOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 bg-ivory transition-shadow duration-300 lg:bg-ivory/95 lg:backdrop-blur-md ${
        scrolled ? "shadow-[0_10px_36px_rgba(17,17,17,0.09)]" : ""
      }`}
    >
      {/* Mobile trust strip */}
      <div data-testid="header-trust-strip" className="bg-charcoal py-2 text-center lg:hidden">
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-ivory/80">
          Servicing Perth Metro ·{" "}
          <button
            type="button"
            data-testid="trust-strip-quote-button"
            onClick={() => openQuote()}
            className="uppercase tracking-[0.22em] text-ember underline decoration-ember/60 underline-offset-2 transition-colors duration-200 active:text-ember-dark"
          >
            Enquire Today
          </button>
        </p>
      </div>

      {/* Mobile: logo + phone */}
      <div className="flex h-14 items-center justify-between px-4 lg:hidden">
        <Link to="/" data-testid="mobile-logo-link" className="flex items-center gap-2.5" aria-label="Modify — home">
          <img src="/images/logo.png" alt="Modify logo — We Care" className="h-9 w-9 rounded-lg" />
          <span className="leading-none">
            <span className="block font-display text-base font-extrabold tracking-[0.12em] text-charcoal">MODIFY</span>
            <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-[0.3em] text-sage-dark">We Care</span>
          </span>
        </Link>
        <a
          href={phoneHref}
          data-testid="mobile-phone-link"
          className="flex min-h-[44px] items-center gap-2 text-sm font-bold text-charcoal transition-colors duration-200 hover:text-sage-dark"
        >
          <Phone className="h-4 w-4 text-ember-dark" aria-hidden="true" />
          {phoneDisplay}
        </a>
      </div>

      {/* Mobile: MENU + GET A QUOTE */}
      <div className="flex items-center gap-3 border-t border-line/60 px-4 py-2.5 lg:hidden">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="mobile-menu-button"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full border border-charcoal/25 bg-white text-xs font-bold uppercase tracking-[0.16em] text-charcoal transition-colors duration-200 hover:border-charcoal"
        >
          {menuOpen ? <X className="h-4 w-4 text-ember" aria-hidden="true" /> : <LayoutGrid className="h-4 w-4 text-ember" aria-hidden="true" />}
          {menuOpen ? "Hide Menu" : "Menu"}
        </button>
        <button
          type="button"
          onClick={() => openQuote()}
          data-testid="mobile-header-quote-button"
          className="flex min-h-[44px] flex-1 items-center justify-center rounded-full bg-ember px-4 text-xs font-bold uppercase tracking-[0.14em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
        >
          Get a Quote
        </button>
      </div>

      {/* Desktop row 1: logo + primary nav + trust items (collapses on scroll) */}
      <div
        className={`hidden overflow-hidden transition-all duration-300 lg:block ${
          scrolled ? "lg:max-h-0 lg:opacity-0" : "lg:max-h-16 lg:opacity-100"
        }`}
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-6 px-6 lg:px-8">
          <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center gap-3" aria-label="Modify — home">
            <img src="/images/logo.png" alt="Modify logo — We Care" className="h-9 w-9 rounded-lg" />
            <span className="leading-none">
              <span className="block font-display text-base font-extrabold tracking-[0.12em] text-charcoal">MODIFY</span>
              <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-[0.3em] text-sage-dark">We Care</span>
            </span>
          </Link>
          <nav aria-label="Primary" className="flex items-center gap-4 whitespace-nowrap xl:gap-5">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${slug(link.label)}-link`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
                    isActive ? "text-charcoal underline decoration-sage decoration-2 underline-offset-8" : "text-ink/55 hover:text-charcoal"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop row 2: ALL SERVICES menu + quick links + phone + quote (stays sticky) */}
      <div className="hidden border-y border-line bg-ivory lg:block">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-6 lg:px-8">
          <div className="flex items-center gap-5 xl:gap-7">
            <div className="relative" ref={servicesRef}>
              <button
                type="button"
                onClick={() => setServicesOpen((v) => !v)}
                data-testid="header-services-menu-button"
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                className="flex min-h-[44px] items-center gap-2 rounded-full bg-ember px-5 text-xs font-bold uppercase tracking-[0.14em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
              >
                <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                All Services
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {servicesOpen && (
                <div
                  data-testid="header-services-dropdown"
                  className="absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl border border-line bg-white p-2 shadow-[0_20px_50px_rgba(17,17,17,0.14)]"
                >
                  {SERVICE_LINKS.map((link) => {
                    const Icon = SERVICE_ICONS[link.label];
                    return (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        data-testid={`nav-service-${slug(link.label)}-link`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-xl px-4 py-3 font-display text-base font-bold transition-colors duration-200 ${
                            isActive ? "bg-sand/60 text-sage-dark" : "text-charcoal hover:bg-ivory"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
                        {link.label}
                      </NavLink>
                    );
                  })}
                  <NavLink
                    to="/gallery"
                    data-testid="nav-service-gallery-link"
                    className="block rounded-xl border-t border-line/60 px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-ink/55 transition-colors duration-200 hover:text-charcoal"
                  >
                    View Our Work
                  </NavLink>
                </div>
              )}
            </div>
          </div>
          <div data-testid="header-trust-items" className="hidden items-center gap-4 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.18em] text-ink/55 xl:flex">
            <span>Servicing Perth Metro</span>
            <span className="h-1 w-1 rotate-45 bg-sage" aria-hidden="true" />
            <span>Enquire Today</span>
            <span className="h-1 w-1 rotate-45 bg-sage" aria-hidden="true" />
            <span>5.0 Google Rating</span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href={phoneHref}
              data-testid="header-phone-link"
              className="flex min-h-[44px] items-center gap-2 text-sm font-bold text-charcoal transition-colors duration-200 hover:text-sage-dark"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ember/15">
                <Phone className="h-4 w-4 text-ember-dark" aria-hidden="true" />
              </span>
              {phoneDisplay}
            </a>
            <button
              type="button"
              onClick={() => openQuote()}
              data-testid="header-quote-button"
              className="flex min-h-[44px] items-center rounded-full bg-ember px-6 text-xs font-bold uppercase tracking-[0.14em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
            >
              Get a Quote
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <nav
          aria-label="Mobile"
          data-testid="mobile-menu"
          className="max-h-[calc(100dvh-150px)] overflow-y-auto border-t border-line bg-ivory lg:hidden"
        >
          <div className="grid grid-cols-4 divide-x divide-line border-b border-line">
            {TOP_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${slug(link.label)}-link`}
                className={({ isActive }) =>
                  `py-4 text-center text-sm font-semibold transition-colors duration-200 ${
                    isActive ? "bg-sand/50 text-sage-dark" : "text-charcoal hover:bg-sand/30"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <p className="px-5 pb-1 pt-6 text-[11px] font-bold uppercase tracking-[0.26em] text-ember-dark">
            Our Services
          </p>
          <ul className="divide-y divide-line/70 border-b border-line/70">
            {SERVICE_LINKS.map((link) => {
              const Icon = SERVICE_ICONS[link.label];
              return (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-${slug(link.label)}-link`}
                    className="group flex items-center gap-4 px-5 py-5 transition-colors duration-200 hover:bg-sand/30"
                  >
                    <Icon className="h-5 w-5 shrink-0 text-ember" aria-hidden="true" />
                    <span className="flex-1 font-display text-[1.35rem] font-bold leading-tight tracking-tight text-charcoal">
                      {link.label}
                    </span>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-ink/30 transition-transform duration-200 group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="mx-4 mb-6 mt-5 rounded-2xl bg-charcoal px-6 py-7 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-sand/70">Get in touch now</p>
            <a
              href={phoneHref}
              data-testid="mobile-menu-phone-link"
              className="mt-3 flex items-center justify-center gap-3 font-display text-2xl font-extrabold tracking-tight text-ivory transition-colors duration-200 hover:text-sand"
            >
              <Phone className="h-5 w-5 text-ember" aria-hidden="true" />
              {phoneDisplay}
            </a>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                openQuote();
              }}
              data-testid="mobile-menu-quote-button"
              className="mt-6 min-h-[52px] w-full rounded-full bg-ember text-sm font-bold uppercase tracking-[0.14em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
            >
              Get a Quote
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
